/**
 * Native JavaScript implementation of the shim interfaces.
 * Uses native Map, Array, and Set with defensive copying for immutability.
 */

import type {
  FromJS,
  ICollection,
  IList,
  IMap,
  IOrderedMap,
  IOrderedSet,
  ISeq,
  ISet,
  RecordFactory,
} from './interfaces';

// ============================================================================
// Helper Types
// ============================================================================

type KeyPath = Iterable<unknown>;

// ============================================================================
// String Hash Function (matches Immutable.js)
// ============================================================================

// Convert to Small Integer (SMI) - matches Immutable.js's smi function
function smi(i32: number): number {
  // eslint-disable-next-line no-bitwise
  return ((i32 >>> 1) & 0x40000000) | (i32 & 0xbfffffff);
}

// Hash a string using Java's String.hashCode algorithm (same as Immutable.js)
function hashString(str: string): number {
  let hashed = 0;
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    hashed = (31 * hashed + str.charCodeAt(i)) | 0;
  }
  return smi(hashed);
}

// HAMT constants matching Immutable.js
const HAMT_SHIFT = 5;
// eslint-disable-next-line no-bitwise
const HAMT_SIZE = 1 << HAMT_SHIFT; // 32
const HAMT_MASK = HAMT_SIZE - 1; // 31
// Immutable.js uses ArrayMapNode for small maps (<=8 entries) which preserves insertion order.
// For larger maps, it uses HAMT which orders by hash bits.
const MAX_ARRAY_MAP_SIZE = HAMT_SIZE / 4; // 8

// Get the bucket index at a given HAMT level (0-indexed)
function getHamtBucket(hash: number, level: number): number {
  // Use unsigned shift for consistent behavior
  // eslint-disable-next-line no-bitwise
  return ((hash >>> 0) >>> (level * HAMT_SHIFT)) & HAMT_MASK;
}

// Compare function for HAMT-like ordering.
// Immutable.js HAMT iterates by traversing buckets 0-31 at each level.
// This is equivalent to sorting by L0, then L1, then L2, etc.
// Which is the same as sorting by the hash bits from LSB to MSB.
function compareByHamtOrder(a: string, b: string): number {
  const hashA = hashString(a);
  const hashB = hashString(b);
  // Compare level by level (L0, L1, L2, etc.)
  for (let level = 0; level < 7; level++) {
    const bucketA = getHamtBucket(hashA, level);
    const bucketB = getHamtBucket(hashB, level);
    if (bucketA !== bucketB) {
      return bucketA - bucketB;
    }
  }
  // If all levels are equal (hash collision), maintain insertion order
  return 0;
}

// ============================================================================
// Deep Path Utilities
// ============================================================================

function getInHelper(obj: unknown, keyPath: KeyPath, notSetValue?: unknown): unknown {
  const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null) {
      return notSetValue;
    }

    if (isNativeMap(current)) {
      current = current.get(key as never, notSetValue as never);
    } else if (isNativeList(current)) {
      current = current.get(key as number, notSetValue as never);
    } else if (typeof current === 'object' && current !== null) {
      current = (current as Record<string, unknown>)[key as string];
    } else {
      return notSetValue;
    }

    if (current === undefined) {
      return notSetValue;
    }
  }

  return current;
}

function setInHelper<T>(obj: T, keyPath: KeyPath, value: unknown): T {
  const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
  if (keys.length === 0) {
    return value as T;
  }

  // Delegate to custom collections that implement setIn (e.g., Record)
  // before falling back to native Map/List/plain object handling.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!isNativeMap(obj) && !isNativeList(obj) && obj && typeof obj === 'object' && 'setIn' in (obj as any)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (obj as any).setIn(keys, value);
  }

  const [firstKey, ...restKeys] = keys;

  if (isNativeMap(obj)) {
    if (restKeys.length === 0) {
      return obj.set(firstKey as never, value as never) as T;
    }
    const nested = obj.get(firstKey as never);
    // Initialize missing nested path with a Map to mirror immutable.js behavior
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialized = nested === undefined || nested === null ? (Map() as any) : nested;
    return obj.set(firstKey as never, setInHelper(initialized, restKeys, value) as never) as T;
  }

  if (isNativeList(obj)) {
    if (restKeys.length === 0) {
      return obj.set(firstKey as number, value as never) as T;
    }
    const nested = obj.get(firstKey as number);
    return obj.set(firstKey as number, setInHelper(nested, restKeys, value) as never) as T;
  }

  // Plain object support: immutably clone and set
  if (obj !== null && typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source: any = obj;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clone: any = Array.isArray(source) ? [...source] : { ...source };
    if (restKeys.length === 0) {
      clone[firstKey as string] = value;
      return clone as T;
    }
    const nested = source[firstKey as string];
    // Initialize missing nested path with an object
    clone[firstKey as string] = setInHelper(nested ?? {}, restKeys, value);
    return clone as T;
  }

  return obj;
}

function updateInHelper<T>(obj: T, keyPath: KeyPath, notSetValue: unknown, updater: (value: unknown) => unknown): T {
  const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
  if (keys.length === 0) {
    return updater(obj) as T;
  }

  const [firstKey, ...restKeys] = keys;

  // Delegate to custom collections that implement updateIn (e.g., Record)
  // before falling back to native Map/List handling.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!isNativeMap(obj) && !isNativeList(obj) && obj && typeof obj === 'object' && 'updateIn' in (obj as any)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (obj as any).updateIn(keys, notSetValue, updater);
  }

  if (isNativeMap(obj)) {
    if (restKeys.length === 0) {
      const currentValue = obj.get(firstKey as never, notSetValue as never);
      return obj.set(firstKey as never, updater(currentValue) as never) as T;
    }
    // For nested paths, if the key doesn't exist and there's no notSetValue,
    // create an empty Map (matching Immutable.js behavior)
    let nested = obj.get(firstKey as never);
    if (nested === undefined) {
      nested = (notSetValue !== undefined ? notSetValue : new NativeMapImpl(new globalThis.Map())) as never;
    }
    return obj.set(firstKey as never, updateInHelper(nested, restKeys, notSetValue, updater) as never) as T;
  }

  if (isNativeList(obj)) {
    if (restKeys.length === 0) {
      const currentValue = obj.get(firstKey as number, notSetValue as never);
      return obj.set(firstKey as number, updater(currentValue) as never) as T;
    }
    const nested = obj.get(firstKey as number, notSetValue as never);
    return obj.set(firstKey as number, updateInHelper(nested, restKeys, notSetValue, updater) as never) as T;
  }

  // Plain object support: immutably clone and update
  if (obj !== null && typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source: any = obj;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clone: any = Array.isArray(source) ? [...source] : { ...source };
    if (restKeys.length === 0) {
      const currentValue = source[firstKey as string] !== undefined ? source[firstKey as string] : notSetValue;
      clone[firstKey as string] = updater(currentValue);
      return clone as T;
    }
    const nested = source[firstKey as string] !== undefined ? source[firstKey as string] : notSetValue;
    clone[firstKey as string] = updateInHelper(nested, restKeys, notSetValue, updater);
    return clone as T;
  }

  return obj;
}

function deleteInHelper<T>(obj: T, keyPath: KeyPath): T {
  const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
  if (keys.length === 0) {
    return obj;
  }

  const [firstKey, ...restKeys] = keys;

  // Delegate to custom collections that implement deleteIn (e.g., Record)
  // before falling back to native Map/List handling.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!isNativeMap(obj) && !isNativeList(obj) && obj && typeof obj === 'object' && 'deleteIn' in (obj as any)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (obj as any).deleteIn(keys);
  }

  if (isNativeMap(obj)) {
    if (restKeys.length === 0) {
      return obj.delete(firstKey as never) as T;
    }
    const nested = obj.get(firstKey as never);
    if (nested !== undefined) {
      return obj.set(firstKey as never, deleteInHelper(nested, restKeys) as never) as T;
    }
  }

  if (isNativeList(obj)) {
    if (restKeys.length === 0) {
      return obj.delete(firstKey as number) as T;
    }
    const nested = obj.get(firstKey as number);
    if (nested !== undefined) {
      return obj.set(firstKey as number, deleteInHelper(nested, restKeys) as never) as T;
    }
  }

  // Plain object support: immutably clone and delete
  if (obj !== null && typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source: any = obj;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clone: any = Array.isArray(source) ? [...source] : { ...source };
    if (restKeys.length === 0) {
      if (Array.isArray(clone) && typeof firstKey === 'number') {
        clone.splice(firstKey, 1);
      } else {
        delete clone[firstKey as string];
      }
      return clone as T;
    }
    const nested = source[firstKey as string];
    if (nested !== undefined) {
      clone[firstKey as string] = deleteInHelper(nested, restKeys);
      return clone as T;
    }
  }

  return obj;
}

function hasInHelper(obj: unknown, keyPath: KeyPath): boolean {
  const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null) {
      return false;
    }

    if (isNativeMap(current)) {
      if (!current.has(key as never)) {
        return false;
      }
      current = current.get(key as never);
    } else if (isNativeList(current)) {
      if (!current.has(key as number)) {
        return false;
      }
      current = current.get(key as number);
    } else if (typeof current === 'object') {
      if (!(key in current)) {
        return false;
      }
      current = (current as Record<string, unknown>)[key as string];
    } else {
      return false;
    }
  }

  return true;
}

// ============================================================================
// Type Guards
// ============================================================================

function isNativeMap(value: unknown): value is NativeMapImpl<unknown, unknown> {
  return value instanceof NativeMapImpl;
}

function isNativeList(value: unknown): value is NativeListImpl<unknown> {
  return value instanceof NativeListImpl;
}

function isNativeSet(value: unknown): value is NativeSetImpl<unknown> {
  return value instanceof NativeSetImpl;
}

function isNativeRecord(value: unknown): boolean {
  // Check if value has toJS method and _data property (Record signature)
  return value !== null && value !== undefined && typeof value === 'object' && 'toJS' in value && '_data' in value;
}

function isNativeCollection(value: unknown): boolean {
  return isNativeMap(value) || isNativeList(value) || isNativeSet(value) || isNativeRecord(value);
}

// ============================================================================
// Sequence Implementation
// ============================================================================

class NativeSeq<T> implements ISeq<T> {
  constructor(private readonly _values: T[]) {}

  *[Symbol.iterator](): Iterator<T> {
    yield* this._values;
  }

  // Read
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(index: number, notSetValue?: any): T | undefined {
    return this._values[index] ?? notSetValue;
  }

  first<NSV>(notSetValue?: NSV): T | NSV;
  first(): T | undefined;
  first<NSV>(notSetValue?: NSV): T | NSV | undefined {
    return this._values[0] ?? notSetValue;
  }

  // Size
  get size(): number {
    return this._values.length;
  }

  count(): number {
    return this._values.length;
  }

  // Transformation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  map<M>(mapper: (value: T, index: number, iter: ISeq<T>) => M, context?: any): ISeq<M> {
    return new NativeSeq(this._values.map((v, i) => mapper.call(context, v, i, this)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter(predicate: (value: T, index: number, iter: ISeq<T>) => boolean, context?: any): ISeq<T> {
    return new NativeSeq(this._values.filter((v, i) => predicate.call(context, v, i, this)));
  }

  flatten(depth?: number | boolean): ISeq<unknown> {
    const actualDepth = typeof depth === 'boolean' ? (depth ? 1 : Infinity) : (depth ?? 1);
    const flattenHelper = (arr: readonly unknown[], d: number): unknown[] => {
      if (d <= 0) {
        return [...arr];
      }
      return arr.reduce<unknown[]>((acc, val) => {
        if (isNativeList(val)) {
          acc.push(...flattenHelper(val.toArray(), d - 1));
        } else {
          acc.push(val);
        }
        return acc;
      }, []);
    };
    return new NativeSeq(flattenHelper(this._values, actualDepth));
  }

  // Grouping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupBy<G>(grouper: (value: T, index: number, iter: ISeq<T>) => G, context?: any): IMap<G, ISeq<T>> {
    const groups = new globalThis.Map<G, T[]>();
    this._values.forEach((value, index) => {
      const group = grouper.call(context, value, index, this);
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      const groupArray = groups.get(group);
      if (groupArray) {
        groupArray.push(value);
      }
    });
    const result = new globalThis.Map<G, ISeq<T>>();
    for (const [group, values] of groups) {
      result.set(group, new NativeSeq(values));
    }
    return new NativeMapImpl(result) as unknown as IMap<G, ISeq<T>>;
  }

  // Iteration
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forEach(fn: (value: T, index: number, iter: ISeq<T>) => void, context?: any): number {
    this._values.forEach((v, i) => fn.call(context, v, i, this));
    return this._values.length;
  }

  // Conversion
  toArray(): T[] {
    return [...this._values];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toJS(): any[] {
    return this.toArray();
  }

  toList(): IList<T> {
    return List(this._values);
  }

  join(separator = ','): string {
    return this._values.join(separator);
  }

  toOrderedMap(): IOrderedMap<number, T> {
    const entries: Array<[number, T]> = this._values.map((value, index) => [index, value]);
    return OrderedMap(entries);
  }
}

// ============================================================================
// Native Map Implementation
// ============================================================================

class NativeMapImpl<K, V> implements IMap<K, V> {
  private readonly _map!: Map<K, V>;
  // Expose size as enumerable own property to match real Immutable.js behavior.
  // This ensures lodash.isEmpty() returns false for Maps (even empty ones),
  // because lodash checks Object.keys(value).length for object-like values.
  // Defined as a getter (not a fixed value) so it reflects mutations during withMutations.
  readonly size!: number;

  constructor(map: Map<K, V>) {
    Object.defineProperty(this, '_map', {
      value: map,
      enumerable: false,
      writable: false,
      configurable: false,
    });
    // Define size as enumerable getter (matches real Immutable.js enumerability
    // while supporting dynamic updates during withMutations)
    Object.defineProperty(this, 'size', {
      get: () => this._map.size,
      enumerable: true,
      configurable: false,
    });
    // Expose string keys as enumerable getters so object spread `{ ...map }` produces a plain object
    for (const [k] of map) {
      if (typeof k === 'string' && !(k in this)) {
        Object.defineProperty(this, k, {
          enumerable: true,
          configurable: false,
          get: () => this._map.get(k as unknown as K),
        });
      }
    }
  }

  // Attempt to find an equivalent key by structural equality when direct lookup fails.
  // This mimics Immutable.js value semantics for keys that are immutable collections (e.g., Record).
  private _findEquivalentKey(searchedKey: K): K | undefined {
    // Fast path: exact key
    if (this._map.has(searchedKey)) {
      return searchedKey;
    }
    for (const existingKey of this._map.keys()) {
      // Value-based equality using shim's is()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (is(existingKey as unknown as any, searchedKey as unknown as any)) {
        return existingKey;
      }
    }
    return undefined;
  }

  // Iterator protocol
  *[Symbol.iterator](): Iterator<[K, V]> {
    yield* this._map;
  }

  // Read
  get(key: K, notSetValue?: V): V | undefined {
    // Fast path
    let value = this._map.get(key);
    if (value !== undefined) {
      return value;
    }
    // If a string getter was defined on the instance, use it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (key as any) === 'string' && Object.prototype.hasOwnProperty.call(this, key as any)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const viaProp = (this as any)[key as any] as V | undefined;
      if (viaProp !== undefined) {
        return viaProp;
      }
    }
    // String key tolerance: boxed vs primitive, or numeric-string vs number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asAny: any = key as unknown as any;
    // 1) If key is a boxed String, unbox
    if (typeof asAny === 'object' && asAny instanceof String) {
      value = this._map.get(String(asAny) as unknown as K);
      if (value !== undefined) {
        return value;
      }
    }
    // 2) If key is a number, try its string form (for maps keyed by string ids/indices)
    if (typeof asAny === 'number') {
      value = this._map.get(String(asAny) as unknown as K);
      if (value !== undefined) {
        return value;
      }
    }
    // 3) If key is a string, try scanning for boxed String keys or equivalent stringification
    if (typeof asAny === 'string') {
      for (const [k, v] of this._map) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const kk: any = k as unknown as any;
        if ((typeof kk === 'object' && kk instanceof String && String(kk) === asAny) || String(k) === asAny) {
          return v;
        }
      }
    }
    // 4) Structural key equivalence (e.g., Record/FormField keys)
    const equivalent = this._findEquivalentKey(key);
    if (equivalent !== undefined) {
      return this._map.get(equivalent);
    }
    return notSetValue;
  }

  // We intentionally don't define Symbol.toStringTag to match real Immutable.js behavior.
  // This ensures lodash.isEmpty() uses Object.keys() check instead of Map-specific !size check.
  getIn(keyPath: KeyPath, notSetValue?: unknown): unknown {
    return getInHelper(this, keyPath, notSetValue);
  }

  has(key: K): boolean {
    if (this._map.has(key)) {
      return true;
    }
    return this._findEquivalentKey(key) !== undefined;
  }

  hasIn(keyPath: KeyPath): boolean {
    return hasInHelper(this, keyPath);
  }

  includes(value: V): boolean {
    for (const v of this._map.values()) {
      if (is(v, value)) {
        return true;
      }
    }
    return false;
  }

  contains(value: V): boolean {
    return this.includes(value);
  }

  first<NSV>(notSetValue?: NSV): V | NSV;
  first(): V | undefined;
  first<NSV>(notSetValue?: NSV): V | NSV | undefined {
    const firstEntry = this._map.entries().next();
    if (firstEntry.done) {
      return notSetValue;
    }
    return firstEntry.value[1];
  }

  // Write (returns new instance)
  set(key: K, value: V): NativeMapImpl<K, V> {
    const newMap = new globalThis.Map(this._map);
    // Replace any structurally equivalent key to avoid duplicate logical keys
    const equivalent = this._findEquivalentKey(key);
    if (equivalent !== undefined && equivalent !== key) {
      newMap.delete(equivalent);
    }
    newMap.set(key, value);
    return new NativeMapImpl<K, V>(newMap);
  }

  setIn(keyPath: KeyPath, value: unknown): IMap<K, V> {
    return setInHelper(this, keyPath, value);
  }

  delete(key: K): IMap<K, V> {
    const newMap = new globalThis.Map(this._map);
    const equivalent = this._findEquivalentKey(key);
    if (equivalent !== undefined) {
      newMap.delete(equivalent);
    } else {
      newMap.delete(key);
    }
    return new NativeMapImpl<K, V>(newMap);
  }

  remove(key: K): IMap<K, V> {
    return this.delete(key);
  }

  deleteIn(keyPath: KeyPath): IMap<K, V> {
    return deleteInHelper(this, keyPath);
  }

  clear(): IMap<K, V> {
    return new NativeMapImpl<K, V>(new globalThis.Map());
  }

  // Update
  update(updater: (value: IMap<K, V>) => IMap<K, V>): IMap<K, V>;
  update<K2 extends K>(key: K2, updater: (value: V) => V): IMap<K, V>;
  update<K2 extends K>(key: K2, notSetValue: V, updater: (value: V) => V): IMap<K, V>;
  update(...args: unknown[]): IMap<K, V> {
    if (args.length === 1) {
      const updater = args[0] as (value: IMap<K, V>) => IMap<K, V>;
      return updater(this);
    }
    if (args.length === 2) {
      const [key, updater] = args as [K, (value: V) => V];
      const value = this.get(key);
      return this.set(key, updater(value as V));
    }
    const [key, notSetValue, updater] = args as [K, V, (value: V) => V];
    const value = this.get(key, notSetValue);
    return this.set(key, updater(value as V));
  }

  updateIn(keyPath: KeyPath, updater: (value: unknown) => unknown): IMap<K, V>;
  updateIn(keyPath: KeyPath, notSetValue: unknown, updater: (value: unknown) => unknown): IMap<K, V>;
  updateIn(...args: unknown[]): IMap<K, V> {
    if (args.length === 2) {
      const [keyPath, updater] = args as [KeyPath, (value: unknown) => unknown];
      return updateInHelper(this, keyPath, undefined, updater);
    }
    const [keyPath, notSetValue, updater] = args as [KeyPath, unknown, (value: unknown) => unknown];
    return updateInHelper(this, keyPath, notSetValue, updater);
  }

  // Merge
  merge(...collections: Array<Iterable<[K, V]> | { [key: string]: V }>): IMap<K, V> {
    // Optimization: if this map is empty and a single native map is provided,
    // return it directly to preserve reference identity (matches immutable.js behavior in some cases).
    if (this._map.size === 0 && collections.length === 1) {
      const only = collections[0];
      if (isNativeMap(only)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return only as any;
      }
    }
    const newMap = new globalThis.Map(this._map);
    for (const collection of collections) {
      if (Symbol.iterator in collection) {
        for (const [key, value] of collection) {
          if (newMap.has(key)) {
            const current = newMap.get(key);
            if (is(current, value)) {
              continue;
            }
          }
          newMap.set(key, value);
        }
      } else {
        for (const [key, value] of Object.entries(collection)) {
          const k = key as unknown as K;
          if (newMap.has(k)) {
            const current = newMap.get(k);
            if (is(current, value as unknown as V)) {
              continue;
            }
          }
          newMap.set(k, value as unknown as V);
        }
      }
    }
    return new NativeMapImpl<K, V>(newMap);
  }

  mergeDeep(...collections: Array<Iterable<[K, V]> | { [key: string]: V }>): IMap<K, V> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let result: IMap<K, V> = this;
    for (const collection of collections) {
      // Guard against primitives - only objects can be iterated
      // This matches Immutable.js behavior where primitives at the collection level are ignored
      if (collection === null || collection === undefined || typeof collection !== 'object') {
        continue;
      }
      const entries = Symbol.iterator in collection ? collection : Object.entries(collection);
      for (const [key, value] of entries as Iterable<[K, V]>) {
        if (value === undefined) {
          continue;
        }
        const existing = result.get(key);

        // Check if value is a data structure (object/array) - primitives should just overwrite
        // This matches Immutable.js deepMerger behavior: only recurse if both are data structures
        const valueIsDataStructure = value !== null && typeof value === 'object';

        // If existing value supports mergeDeep (e.g., Record), delegate to it,
        // normalizing the incoming value to native collections when possible.
        if (valueIsDataStructure && existing && typeof existing === 'object' && 'mergeDeep' in (existing as object)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const normalize = (v: any) => {
            if (isNativeCollection(v)) {
              return v;
            }
            if (Array.isArray(v)) {
              return List(v);
            }
            if (v && typeof v === 'object') {
              return Map(v);
            }
            return v;
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const merged = (existing as any).mergeDeep(normalize(value));
          result = result.set(key, merged as unknown as V);
          continue;
        }

        // Check if both are map-like structures (either IMap or plain object)
        if (isNativeMap(existing) && valueIsDataStructure) {
          if (isNativeMap(value)) {
            // Both are Maps - recursively merge
            result = result.set(
              key,
              (existing as NativeMapImpl<unknown, unknown>).mergeDeep(
                value.toObject() as { [key: string]: unknown },
              ) as unknown as V,
            );
          } else if (!Array.isArray(value)) {
            // Existing is Map, value is plain object - merge the object into the map
            result = result.set(
              key,
              (existing as NativeMapImpl<unknown, unknown>).mergeDeep(
                value as { [key: string]: unknown },
              ) as unknown as V,
            );
          } else {
            result = result.set(key, value);
          }
        } else {
          result = result.set(key, value);
        }
      }
    }
    return result;
  }

  mergeWith(
    merger: (oldVal: V, newVal: V, key: K) => V,
    ...collections: Array<Iterable<[K, V]> | { [key: string]: V }>
  ): IMap<K, V> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let result: IMap<K, V> = this;
    for (const collection of collections) {
      const entries = Symbol.iterator in collection ? collection : Object.entries(collection);
      for (const [key, newVal] of entries as Iterable<[K, V]>) {
        const oldVal = result.get(key);
        const mergedVal = oldVal !== undefined ? merger(oldVal, newVal, key) : newVal;
        result = result.set(key, mergedVal);
      }
    }
    return result;
  }

  // Transformation
  map<M>(mapper: (value: V, key: K, iter: IMap<K, V>) => M, context?: unknown): IMap<K, M> {
    const newMap = new globalThis.Map<K, M>();
    for (const [key, value] of this._map) {
      newMap.set(key, mapper.call(context, value, key, this));
    }
    return new NativeMapImpl<K, M>(newMap);
  }

  filter(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): IMap<K, V> {
    const newMap = new globalThis.Map<K, V>();
    for (const [key, value] of this._map) {
      if (predicate.call(context, value, key, this)) {
        newMap.set(key, value);
      }
    }
    return new NativeMapImpl<K, V>(newMap);
  }

  filterNot(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): IMap<K, V> {
    return this.filter((v, k, i) => !predicate.call(context, v, k, i), context);
  }

  // Sorting - returns OrderedMap since sorting creates order
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sort(comparator?: (a: any, b: any) => number): IOrderedMap<K, V> {
    const entries = Array.from(this._map.entries());
    entries.sort((a, b) => (comparator ? comparator(a[1], b[1]) : 0));
    return new NativeMapImpl(new globalThis.Map(entries)) as unknown as IOrderedMap<K, V>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortBy<C>(
    comparatorValueMapper: (value: V, key: K, iter: IMap<K, V>) => C,
    comparator?: (a: C, b: C) => number,
  ): IOrderedMap<K, V> {
    const entries = Array.from(this._map.entries());
    entries.sort((a, b) => {
      const aVal = comparatorValueMapper(a[1], a[0], this);
      const bVal = comparatorValueMapper(b[1], b[0], this);
      return comparator ? comparator(aVal, bVal) : aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });
    return new NativeMapImpl(new globalThis.Map(entries)) as unknown as IOrderedMap<K, V>;
  }

  // Ordering - returns OrderedMap since reversing creates order
  reverse(): IOrderedMap<K, V> {
    const entries = Array.from(this._map.entries()).reverse();
    return new NativeMapImpl(new globalThis.Map(entries)) as unknown as IOrderedMap<K, V>;
  }

  // Grouping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupBy<G>(grouper: (value: V, key: K, iter: IMap<K, V>) => G, context?: any): IMap<G, IMap<K, V>> {
    const groups = new globalThis.Map<G, Array<[K, V]>>();
    for (const [key, value] of this._map) {
      const group = grouper.call(context, value, key, this);
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      const groupArray = groups.get(group);
      if (groupArray) {
        groupArray.push([key, value]);
      }
    }
    const result = new globalThis.Map<G, IMap<K, V>>();
    for (const [group, entries] of groups) {
      result.set(group, new NativeMapImpl(new globalThis.Map(entries)));
    }
    return new NativeMapImpl(result) as unknown as IMap<G, IMap<K, V>>;
  }

  // Advanced Map operations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapKeys<NK>(mapper: (key: K, value: V, iter: IMap<K, V>) => NK): IMap<NK, V> {
    const entries: Array<[NK, V]> = [];
    for (const [key, value] of this._map) {
      entries.push([mapper(key, value, this), value]);
    }
    return new NativeMapImpl(new globalThis.Map(entries)) as unknown as IMap<NK, V>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergeIn(keyPath: Iterable<any>, ...collections: any[]): IMap<K, V> {
    const existing = this.getIn(keyPath);
    // Delegate to any nested collection that supports merge (Map, Record, etc.)
    if (existing && typeof existing === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const maybeMergeable = existing as any;
      if (typeof maybeMergeable.merge === 'function') {
        // Pass-through collections as-is; nested merge impls handle normalization
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const merged = maybeMergeable.merge(...collections);
        return this.setIn(keyPath, merged);
      }
      // If the nested value is a plain object, normalize to a Map and merge there
      if (!isNativeCollection(existing) && !Array.isArray(existing)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const normalized = Map(existing as any);
        return this.setIn(keyPath, normalized.merge(...collections) as unknown as V);
      }
    }
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  flatten(_depth?: number, _shallow?: boolean): IMap<any, any> {
    // Simplified flatten - just returns self for now (complex operation)
    return this as unknown as IMap<any, any>;
  }

  toSet(): ISet<V> {
    return Set(this._map.values());
  }

  toOrderedSet(): IOrderedSet<V> {
    return OrderedSet(this._map.values());
  }

  // Reduce
  reduce<R>(
    reducer: (reduction: R, value: V, key: K, iter: IMap<K, V>) => R,
    initialReduction: R,
    context?: unknown,
  ): R;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reduce<R>(reducer: (reduction: V | R, value: V, key: K, iter: IMap<K, V>) => R): R;
  reduce<R>(
    reducer: (reduction: R | V, value: V, key: K, iter: IMap<K, V>) => R,
    initialReduction?: R,
    context?: unknown,
  ): R {
    if (initialReduction === undefined) {
      // No initial value - use first element
      const iterator = this._map.entries();
      const first = iterator.next();
      if (first.done) {
        throw new TypeError('Reduce of empty Map with no initial value');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let reduction: any = first.value[1];
      for (const [key, value] of iterator) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reduction = reducer.call(context, reduction, value, key, this);
      }
      return reduction;
    }
    let reduction: R = initialReduction;
    for (const [key, value] of this._map) {
      reduction = reducer.call(context, reduction, value, key, this);
    }
    return reduction;
  }

  // Search
  find(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown, notSetValue?: V): V | undefined {
    for (const [key, value] of this._map) {
      if (predicate.call(context, value, key, this)) {
        return value;
      }
    }
    return notSetValue;
  }

  findIndex(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): K | undefined {
    for (const [key, value] of this._map) {
      if (predicate.call(context, value, key, this)) {
        return key;
      }
    }
    return undefined;
  }

  findKey(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): K | undefined {
    return this.findIndex(predicate, context);
  }

  some(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): boolean {
    for (const [key, value] of this._map) {
      if (predicate.call(context, value, key, this)) {
        return true;
      }
    }
    return false;
  }

  every(predicate: (value: V, key: K, iter: IMap<K, V>) => boolean, context?: unknown): boolean {
    for (const [key, value] of this._map) {
      if (!predicate.call(context, value, key, this)) {
        return false;
      }
    }
    return true;
  }

  // Iteration
  forEach(fn: (value: V, key: K, iter: IMap<K, V>) => void, context?: unknown): number {
    let count = 0;
    for (const [key, value] of this._map) {
      fn.call(context, value, key, this);
      count++;
    }
    return count;
  }

  // Sequences
  keySeq(): ISeq<K> {
    return new NativeSeq(Array.from(this._map.keys()));
  }

  valueSeq(): ISeq<V> {
    return new NativeSeq(Array.from(this._map.values()));
  }

  entrySeq(): ISeq<[K, V]> {
    return new NativeSeq(Array.from(this._map.entries()));
  }

  keys(): IterableIterator<K> {
    return this._map.keys();
  }

  values(): IterableIterator<V> {
    return this._map.values();
  }

  entries(): IterableIterator<[K, V]> {
    return this._map.entries();
  }

  isEmpty(): boolean {
    return this._map.size === 0;
  }

  count(): number {
    return this._map.size;
  }

  // Conversion
  toObject(): { [key: string]: V } {
    const obj: { [key: string]: V } = {};
    for (const [key, value] of this._map) {
      obj[String(key)] = value;
    }
    return obj;
  }

  toArray(): Array<[K, V]> {
    return Array.from(this._map);
  }

  toJS(): { [key: string]: unknown } {
    const obj: { [key: string]: unknown } = {};
    for (const [key, value] of this._map) {
      obj[String(key)] = toJSHelper(value);
    }
    return obj;
  }

  toJSON(): { [key: string]: V } {
    return this.toObject();
  }

  toList(): IList<V> {
    return List(Array.from(this._map.values()));
  }

  toMap(): IMap<K, V> {
    // Identity operation - returns a copy of itself
    return new NativeMapImpl(new globalThis.Map(this._map));
  }

  toOrderedMap(): IOrderedMap<K, V> {
    // Convert to OrderedMap preserving insertion order
    return OrderedMap(this._map);
  }

  join(separator = ','): string {
    return Array.from(this._map.values()).join(separator);
  }

  // Batch mutations
  withMutations(mutator: (mutable: IMap<K, V>) => unknown): IMap<K, V> {
    // Create a mutable copy
    const mutableMap = new globalThis.Map(this._map);
    const mutable = new NativeMapImpl<K, V>(mutableMap);

    // Keep a reference to the internal map for efficient mutations
    const internalMap = mutableMap;

    // Override methods to mutate in place
    // @ts-expect-error - Override changes return type for mutation batching
    mutable.set = function (key: K, value: V): IMap<K, V> {
      internalMap.set(key, value);
      return this;
    };
    mutable.delete = function (key: K): IMap<K, V> {
      internalMap.delete(key);
      return this;
    };
    // Mutating merge: apply entries into internalMap and return self
    mutable.merge = function (...collections: Array<Iterable<[K, V]> | { [key: string]: V }>): IMap<K, V> {
      for (const collection of collections) {
        const entries = Symbol.iterator in collection ? collection : (Object.entries(collection) as Array<[K, V]>);
        for (const [k, v] of entries) {
          if (v === undefined) {
            continue;
          }
          internalMap.set(k, v);
        }
      }
      return this;
    };
    // Mutating mergeDeep: delegate to mergeDeep semantics but write into internalMap
    mutable.mergeDeep = function (...collections: Array<Iterable<[K, V]> | { [key: string]: V }>): IMap<K, V> {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isPlainObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v) && !isNativeCollection(v);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalize = (v: any) => {
        if (isNativeCollection(v)) {
          return v;
        }
        if (Array.isArray(v)) {
          return List(v);
        }
        if (isPlainObject(v)) {
          return Map(v);
        }
        return v;
      };
      for (const collection of collections) {
        const entries = Symbol.iterator in collection ? collection : (Object.entries(collection) as Array<[K, V]>);
        for (const [k, rawV] of entries) {
          if (rawV === undefined) {
            continue;
          }
          const v = normalize(rawV);
          const existing = internalMap.get(k);
          if (existing && typeof existing === 'object' && 'mergeDeep' in (existing as object)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            internalMap.set(k, (existing as any).mergeDeep(v));
            continue;
          }
          if (isNativeMap(existing) && (isNativeMap(v) || isPlainObject(v))) {
            const merged = (existing as unknown as NativeMapImpl<unknown, unknown>).mergeDeep(
              isNativeMap(v) ? v.toObject() : (v as unknown as { [key: string]: unknown }),
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            internalMap.set(k, merged as any);
            continue;
          }
          internalMap.set(k, v as V);
        }
      }
      return this;
    };

    mutator(mutable);

    // Return new immutable version with the mutated map
    return new NativeMapImpl<K, V>(new globalThis.Map(internalMap));
  }

  asMutable(): IMap<K, V> {
    return this;
  }

  asImmutable(): IMap<K, V> {
    return this;
  }

  // Comparison
  equals(other: unknown): boolean {
    // Map vs Map
    if (isNativeMap(other)) {
      if (this._map.size !== other._map.size) {
        return false;
      }
      for (const [key, value] of this._map) {
        if (!other.has(key) || !is(value, other.get(key))) {
          return false;
        }
      }
      return true;
    }
    // Map vs plain object (to support deep equality against object literals with asymmetric matchers)
    if (other && typeof other === 'object' && Object.getPrototypeOf(other) === Object.prototype) {
      const keys = Object.keys(other);
      if (this._map.size !== keys.length) {
        return false;
      }
      for (const [key, value] of this._map) {
        if (!Object.prototype.hasOwnProperty.call(other, String(key))) {
          return false;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const otherValue = (other as any)[String(key)];
        if (!is(value, otherValue)) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  hashCode(): number {
    // Simple hash implementation
    let hash = 0;
    for (const [key, value] of this._map) {
      // eslint-disable-next-line no-bitwise
      hash = (hash + hashHelper(key) + hashHelper(value)) | 0;
    }
    return hash;
  }
}

// ============================================================================
// Native List Implementation
// ============================================================================

class NativeListImpl<T> implements IList<T> {
  private readonly _array!: readonly T[];
  // Expose size as enumerable own property to match real Immutable.js behavior.
  // Defined as a getter (not a fixed value) so it reflects mutations during withMutations.
  readonly size!: number;

  constructor(array: readonly T[]) {
    Object.defineProperty(this, '_array', {
      value: array,
      enumerable: false,
      writable: false,
      configurable: false,
    });
    // Define size as enumerable getter (matches real Immutable.js enumerability
    // while supporting dynamic updates during withMutations)
    Object.defineProperty(this, 'size', {
      get: () => this._array.length,
      enumerable: true,
      configurable: false,
    });
  }

  // Iterator protocol
  *[Symbol.iterator](): Iterator<T> {
    yield* this._array;
  }

  // Read
  get(index: number, notSetValue?: T): T | undefined {
    const value = this._array[index];
    return value !== undefined ? value : notSetValue;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIn(keyPath: Iterable<any>, notSetValue?: any): any {
    return getInHelper(this, keyPath, notSetValue);
  }

  has(index: number): boolean {
    return index >= 0 && index < this._array.length;
  }

  includes(value: T): boolean {
    return this._array.some((v) => is(v, value));
  }

  contains(value: T): boolean {
    return this.includes(value);
  }

  first<NSV>(notSetValue?: NSV): T | NSV;
  first(): T | undefined;
  first<NSV>(notSetValue?: NSV): T | NSV | undefined {
    return this._array[0] ?? notSetValue;
  }

  last(): T | undefined {
    return this._array[this._array.length - 1];
  }

  // Write (returns new instance)
  set(index: number, value: T): IList<T> {
    const newArray = [...this._array];
    newArray[index] = value;
    return new NativeListImpl(newArray) as unknown as IList<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setIn(keyPath: Iterable<any>, value: any): IList<T> {
    return setInHelper(this as unknown as IList<T>, keyPath, value);
  }

  delete(index: number): IList<T> {
    const newArray = [...this._array];
    newArray.splice(index, 1);
    return new NativeListImpl(newArray) as unknown as IList<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deleteIn(keyPath: Iterable<any>): IList<T> {
    return deleteInHelper(this as unknown as IList<T>, keyPath);
  }

  remove(index: number): IList<T> {
    return this.delete(index);
  }

  clear(): IList<T> {
    return new NativeListImpl<T>([]) as unknown as IList<T>;
  }

  push(...values: T[]): IList<T> {
    return new NativeListImpl([...this._array, ...values]) as unknown as IList<T>;
  }

  pop(): IList<T> {
    return new NativeListImpl(this._array.slice(0, -1)) as unknown as IList<T>;
  }

  unshift(...values: T[]): IList<T> {
    return new NativeListImpl([...values, ...this._array]) as unknown as IList<T>;
  }

  shift(): IList<T> {
    return new NativeListImpl(this._array.slice(1)) as unknown as IList<T>;
  }

  // Update
  update(updater: (value: IList<T>) => IList<T>): IList<T>;
  update(index: number, updater: (value: T) => T): IList<T>;
  update(index: number, notSetValue: T, updater: (value: T) => T): IList<T>;
  update(...args: unknown[]): IList<T> {
    if (args.length === 1) {
      const updater = args[0] as (value: IList<T>) => IList<T>;
      return updater(this as unknown as IList<T>);
    }
    if (args.length === 2) {
      const [index, updater] = args as [number, (value: T) => T];
      const value = this.get(index);
      return this.set(index, updater(value as T));
    }
    const [index, notSetValue, updater] = args as [number, T, (value: T) => T];
    const value = this.get(index, notSetValue);
    return this.set(index, updater(value as T));
  }

  updateIn(keyPath: KeyPath, updater: (value: unknown) => unknown): IList<T>;
  updateIn(keyPath: KeyPath, notSetValue: unknown, updater: (value: unknown) => unknown): IList<T>;
  updateIn(...args: unknown[]): IList<T> {
    if (args.length === 2) {
      const [keyPath, updater] = args as [KeyPath, (value: unknown) => unknown];
      return updateInHelper(this as unknown as IList<T>, keyPath, undefined, updater);
    }
    const [keyPath, notSetValue, updater] = args as [KeyPath, unknown, (value: unknown) => unknown];
    return updateInHelper(this as unknown as IList<T>, keyPath, notSetValue, updater);
  }

  // Transformation
  map<M>(mapper: (value: T, index: number, iter: IList<T>) => M, context?: unknown): IList<M> {
    const newArray = this._array.map((value, index) => mapper.call(context, value, index, this as unknown as IList<T>));
    return new NativeListImpl(newArray) as unknown as IList<M>;
  }

  flatMap<M>(mapper: (value: T, index: number, iter: IList<T>) => Iterable<M>, context?: unknown): IList<M> {
    const results: M[] = [];
    this._array.forEach((value, index) => {
      const mapped = mapper.call(context, value, index, this as unknown as IList<T>);
      results.push(...mapped);
    });
    return new NativeListImpl(results) as unknown as IList<M>;
  }

  filter(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): IList<T> {
    const newArray = this._array.filter((value, index) =>
      predicate.call(context, value, index, this as unknown as IList<T>),
    );
    return new NativeListImpl(newArray) as unknown as IList<T>;
  }

  filterNot(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): IList<T> {
    return this.filter((v, i, iter) => !predicate.call(context, v, i, iter), context);
  }

  flatten(depth?: number | boolean): IList<unknown> {
    const actualDepth = typeof depth === 'boolean' ? (depth ? 1 : Infinity) : (depth ?? 1);
    const flattenHelper = (arr: readonly unknown[], d: number): unknown[] => {
      if (d <= 0) {
        return [...arr];
      }
      return arr.reduce<unknown[]>((acc, val) => {
        // Match immutable.js: only flatten IList instances, not plain arrays
        if (isNativeList(val)) {
          acc.push(...flattenHelper(val._array, d - 1));
        } else {
          acc.push(val);
        }
        return acc;
      }, []);
    };
    return new NativeListImpl(flattenHelper(this._array, actualDepth));
  }

  // Reduce
  reduce<R>(
    reducer: (reduction: R, value: T, index: number, iter: IList<T>) => R,
    initialReduction: R,
    context?: unknown,
  ): R;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reduce<R>(reducer: (reduction: T | R, value: T, index: number, iter: IList<T>) => R): R;
  reduce<R>(
    reducer: (reduction: R | T, value: T, index: number, iter: IList<T>) => R,
    initialReduction?: R,
    context?: unknown,
  ): R {
    if (initialReduction === undefined) {
      // No initial value - manually reduce using first element as initial
      if (this._array.length === 0) {
        throw new TypeError('Reduce of empty List with no initial value');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let reduction: any = this._array[0];
      for (let i = 1; i < this._array.length; i++) {
        reduction = reducer.call(context, reduction, this._array[i], i, this as unknown as IList<T>);
      }
      return reduction;
    }
    return this._array.reduce<R>(
      (acc, value, index) => reducer.call(context, acc, value, index, this as unknown as IList<T>),
      initialReduction,
    );
  }

  reduceRight<R>(
    reducer: (reduction: R, value: T, index: number, iter: IList<T>) => R,
    initialReduction: R,
    context?: unknown,
  ): R {
    return this._array.reduceRight(
      (acc, value, index) => reducer.call(context, acc, value, index, this as unknown as IList<T>),
      initialReduction,
    );
  }

  // Search
  find(
    predicate: (value: T, index: number, iter: IList<T>) => boolean,
    context?: unknown,
    notSetValue?: T,
  ): T | undefined {
    const found = this._array.find((value, index) =>
      predicate.call(context, value, index, this as unknown as IList<T>),
    );
    return found !== undefined ? found : notSetValue;
  }

  findIndex(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): number {
    return this._array.findIndex((value, index) => predicate.call(context, value, index, this as unknown as IList<T>));
  }

  findLastIndex(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): number {
    for (let i = this._array.length - 1; i >= 0; i--) {
      if (predicate.call(context, this._array[i], i, this)) {
        return i;
      }
    }
    return -1;
  }

  findLast(
    predicate: (value: T, index: number, iter: IList<T>) => boolean,
    context?: unknown,
    notSetValue?: T,
  ): T | undefined {
    const index = this.findLastIndex(predicate, context);
    return index >= 0 ? this._array[index] : notSetValue;
  }

  indexOf(value: T): number {
    return this._array.findIndex((v) => is(v, value));
  }

  lastIndexOf(value: T): number {
    for (let i = this._array.length - 1; i >= 0; i--) {
      if (is(this._array[i], value)) {
        return i;
      }
    }
    return -1;
  }

  some(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): boolean {
    return this._array.some((value, index) => predicate.call(context, value, index, this as unknown as IList<T>));
  }

  every(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): boolean {
    return this._array.every((value, index) => predicate.call(context, value, index, this as unknown as IList<T>));
  }

  // Iteration
  forEach(fn: (value: T, index: number, iter: IList<T>) => void, context?: unknown): number {
    this._array.forEach((value, index) => fn.call(context, value, index, this));
    return this._array.length;
  }

  // Sequences
  keySeq(): ISeq<number> {
    return new NativeSeq(Array.from(this._array.keys()));
  }

  valueSeq(): ISeq<T> {
    return new NativeSeq(Array.from(this._array));
  }

  entrySeq(): ISeq<[number, T]> {
    return new NativeSeq(this._array.map((value, index) => [index, value] as [number, T]));
  }

  keys(): IterableIterator<number> {
    return this._array.keys();
  }

  values(): IterableIterator<T> {
    return this._array.values();
  }

  entries(): IterableIterator<[number, T]> {
    return this._array.entries();
  }

  // Slicing
  slice(begin?: number, end?: number): IList<T> {
    return new NativeListImpl(this._array.slice(begin, end));
  }

  take(amount: number): IList<T> {
    return this.slice(0, amount);
  }

  takeLast(amount: number): IList<T> {
    return this.slice(-amount);
  }

  takeWhile(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): IList<T> {
    const index = this._array.findIndex((value, i) => !predicate.call(context, value, i, this));
    return index >= 0 ? this.slice(0, index) : this;
  }

  drop(amount: number): IList<T> {
    return this.slice(amount);
  }

  dropLast(amount: number): IList<T> {
    return this.slice(0, -amount);
  }

  dropWhile(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): IList<T> {
    const index = this._array.findIndex((value, i) => !predicate.call(context, value, i, this));
    return index >= 0 ? this.slice(index) : (new NativeListImpl<T>([]) as this);
  }

  skipWhile(predicate: (value: T, index: number, iter: IList<T>) => boolean, context?: unknown): IList<T> {
    return this.dropWhile(predicate, context);
  }

  // Sorting
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sort(comparator?: (a: any, b: any) => number): IList<T> {
    const sorted = [...this._array].sort(comparator);
    return new NativeListImpl(sorted) as unknown as IList<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortBy<C>(
    comparatorValueMapper: (value: T, index: number, iter: IList<T>) => C,
    comparator?: (a: C, b: C) => number,
  ): IList<T> {
    const indexed = this._array.map((val, idx) => ({ val, idx, mapped: comparatorValueMapper(val, idx, this) }));
    indexed.sort((a, b) => {
      return comparator ? comparator(a.mapped, b.mapped) : a.mapped < b.mapped ? -1 : a.mapped > b.mapped ? 1 : 0;
    });
    return new NativeListImpl(indexed.map((item) => item.val));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  maxBy<C>(
    comparatorValueMapper: (value: T, index: number, iter: IList<T>) => C,
    comparator?: (a: C, b: C) => number,
  ): T | undefined {
    if (this._array.length === 0) {
      return undefined;
    }
    let maxItem = this._array[0];
    let maxValue = comparatorValueMapper(maxItem, 0, this);
    for (let i = 1; i < this._array.length; i++) {
      const item = this._array[i];
      const value = comparatorValueMapper(item, i, this);
      const cmp = comparator ? comparator(value, maxValue) : value > maxValue ? 1 : value < maxValue ? -1 : 0;
      if (cmp > 0) {
        maxItem = item;
        maxValue = value;
      }
    }
    return maxItem;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  minBy<C>(
    comparatorValueMapper: (value: T, index: number, iter: IList<T>) => C,
    comparator?: (a: C, b: C) => number,
  ): T | undefined {
    if (this._array.length === 0) {
      return undefined;
    }
    let minItem = this._array[0];
    let minValue = comparatorValueMapper(minItem, 0, this);
    for (let i = 1; i < this._array.length; i++) {
      const item = this._array[i];
      const value = comparatorValueMapper(item, i, this);
      const cmp = comparator ? comparator(value, minValue) : value < minValue ? -1 : value > minValue ? 1 : 0;
      if (cmp < 0) {
        minItem = item;
        minValue = value;
      }
    }
    return minItem;
  }

  // Grouping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupBy<G>(grouper: (value: T, index: number, iter: IList<T>) => G, context?: any): IMap<G, IList<T>> {
    const groups = new globalThis.Map<G, T[]>();
    this._array.forEach((value, index) => {
      const group = grouper.call(context, value, index, this);
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      const groupArray = groups.get(group);
      if (groupArray) {
        groupArray.push(value);
      }
    });
    const result = new globalThis.Map<G, IList<T>>();
    for (const [group, values] of groups) {
      result.set(group, new NativeListImpl(values));
    }
    return new NativeMapImpl(result) as unknown as IMap<G, IList<T>>;
  }

  // Combination
  insert(index: number, value: T): IList<T> {
    const newArray = [...this._array];
    newArray.splice(index, 0, value);
    return new NativeListImpl(newArray) as unknown as IList<T>;
  }

  splice(index: number, removeNum: number, ...values: T[]): IList<T> {
    const newArray = [...this._array];
    newArray.splice(index, removeNum, ...values);
    return new NativeListImpl(newArray) as unknown as IList<T>;
  }

  reverse(): IList<T> {
    return new NativeListImpl([...this._array].reverse());
  }

  concat<C>(...valuesOrCollections: Array<Iterable<C> | C>): IList<T | C> {
    const result: Array<T | C> = [...this._array];
    for (const item of valuesOrCollections) {
      if (item && typeof item === 'object' && Symbol.iterator in item) {
        result.push(...item);
      } else {
        result.push(item);
      }
    }
    return new NativeListImpl(result) as unknown as IList<T | C>;
  }

  merge<C>(...collections: Array<Iterable<C>>): IList<T | C> {
    // immutable.js List.merge() behaves like concat - appends elements
    return this.concat(...collections);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zipAll(...collections: Array<Iterable<any>>): IList<any> {
    const allCollections = [this._array, ...collections.map((c) => Array.from(c))];
    const maxLength = Math.max(...allCollections.map((c) => c.length));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any[] = [];

    for (let i = 0; i < maxLength; i++) {
      const row: unknown[] = [];
      for (const collection of allCollections) {
        row.push(i < collection.length ? collection[i] : undefined);
      }
      result.push(row);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new NativeListImpl(result) as unknown as IList<any>;
  }

  toIndexedSeq(): ISeq<T> {
    return new NativeSeq(Array.from(this._array));
  }

  // Deep merge by index for List: element-wise, delegating when possible
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergeDeep<C>(...collections: Array<Iterable<C>>): IList<T | C> {
    const result: Array<T | C> = [...this._array];
    const normalize = (v: unknown) => {
      if (isNativeCollection(v)) {
        return v;
      }
      if (Array.isArray(v)) {
        return List(v);
      }
      if (v && typeof v === 'object') {
        return Map(v as { [key: string]: unknown });
      }
      return v;
    };
    const assignDeep = (idx: number, incoming: unknown) => {
      const existing = result[idx];
      const right = normalize(incoming);
      if (existing && typeof existing === 'object' && 'mergeDeep' in (existing as object)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result[idx] = (existing as any).mergeDeep(right);
        return;
      }
      if (isNativeMap(existing) && isNativeMap(right)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result[idx] = (existing as unknown as NativeMapImpl<unknown, unknown>).mergeDeep(
          (right as unknown as NativeMapImpl<unknown, unknown>).toObject(),
        ) as any;
        return;
      }
      // Fallback: replace
      result[idx] = right as T | C;
    };
    for (const collection of collections) {
      // First pass: detect if all items are valid entries (no side effects)
      const pendingEntries: Array<[number, C]> = [];
      let allAreEntries = true;
      for (const item of collection as Iterable<unknown>) {
        if (Array.isArray(item) && item.length === 2) {
          const [k, v] = item as [number | string, C];
          const n = typeof k === 'string' ? parseInt(k, 10) : k;
          if (!Number.isNaN(n) && n >= 0) {
            pendingEntries.push([n, v]);
          } else {
            allAreEntries = false;
            break;
          }
        } else {
          allAreEntries = false;
          break;
        }
      }
      // Second pass: apply based on detection result
      if (allAreEntries && pendingEntries.length > 0) {
        for (const [n, v] of pendingEntries) {
          assignDeep(n, v);
        }
      } else {
        let i = 0;
        for (const v of collection) {
          assignDeep(i, v);
          i++;
        }
      }
    }
    return new NativeListImpl(result) as unknown as IList<T | C>;
  }

  // Note: size is defined as an enumerable own property in constructor (matches Immutable.js)

  isEmpty(): boolean {
    return this._array.length === 0;
  }

  count(): number {
    return this._array.length;
  }

  // Conversion
  toArray(): T[] {
    return [...this._array];
  }

  toJS(): unknown[] {
    return this._array.map(toJSHelper);
  }

  toJSON(): T[] {
    return this.toArray();
  }

  toSet(): ISet<T> {
    return Set(this._array);
  }

  toOrderedSet(): IOrderedSet<T> {
    return OrderedSet(this._array);
  }

  toMap(): IMap<number, T> {
    const entries: Array<[number, T]> = this._array.map((v, i) => [i, v]);
    return Map(entries);
  }

  toList(): IList<T> {
    return this;
  }

  join(separator = ','): string {
    return this._array.join(separator);
  }

  // Batch mutations
  withMutations(mutator: (mutable: IList<T>) => unknown): IList<T> {
    const mutableArray = [...this._array];
    const mutable = new NativeListImpl<T>(mutableArray);

    // Override methods to mutate in place
    mutable.set = function (index: number, value: T): IList<T> {
      mutableArray[index] = value;
      return this;
    };
    mutable.push = function (...values: T[]): IList<T> {
      mutableArray.push(...values);
      return this;
    };
    mutable.delete = function (index: number): IList<T> {
      mutableArray.splice(index, 1);
      return this;
    };

    mutator(mutable);

    return new NativeListImpl<T>(mutableArray);
  }

  asMutable(): IList<T> {
    return this;
  }

  asImmutable(): IList<T> {
    return this;
  }

  // Comparison
  equals(other: unknown): boolean {
    if (!isNativeList(other)) {
      return false;
    }
    if (this._array.length !== other._array.length) {
      return false;
    }
    for (let i = 0; i < this._array.length; i++) {
      if (!is(this._array[i], other._array[i])) {
        return false;
      }
    }
    return true;
  }

  hashCode(): number {
    let hash = 0;
    for (const value of this._array) {
      // eslint-disable-next-line no-bitwise
      hash = (hash + hashHelper(value)) | 0;
    }
    return hash;
  }
}

// ============================================================================
// Native Set Implementation
// ============================================================================

class NativeSetImpl<T> implements ISet<T> {
  private readonly _set!: Set<T>;
  constructor(set: Set<T>) {
    Object.defineProperty(this, '_set', {
      value: set,
      enumerable: false,
      writable: false,
      configurable: false,
    });
  }

  // Iterator protocol
  *[Symbol.iterator](): Iterator<T> {
    yield* this._set;
  }

  // Expose Set-like shape for deep equality paths
  get size(): number {
    return this._set.size;
  }
  get [Symbol.toStringTag](): string {
    return 'Set';
  }

  // Read
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIn(keyPath: Iterable<any>, notSetValue?: any): any {
    return getInHelper(this, keyPath, notSetValue);
  }

  has(value: T): boolean {
    for (const v of this._set) {
      if (is(v, value)) {
        return true;
      }
    }
    return false;
  }

  includes(value: T): boolean {
    return this.has(value);
  }

  contains(value: T): boolean {
    return this.has(value);
  }

  get(value: T, notSetValue?: T): T | undefined {
    return this.has(value) ? value : notSetValue;
  }

  // Write (returns new instance)
  add(value: T): ISet<T> {
    const newSet = new globalThis.Set(this._set);
    newSet.add(value);
    return new NativeSetImpl(newSet) as unknown as ISet<T>;
  }

  delete(value: T): ISet<T> {
    const newSet = new globalThis.Set(this._set);
    // Find and delete using value equality
    for (const v of newSet) {
      if (is(v, value)) {
        newSet.delete(v);
        break;
      }
    }
    return new NativeSetImpl<T>(newSet);
  }

  remove(value: T): ISet<T> {
    return this.delete(value);
  }

  clear(): ISet<T> {
    return new NativeSetImpl<T>(new globalThis.Set());
  }

  // Set operations
  union(...collections: Array<Iterable<T>>): ISet<T> {
    // Optimization: if this set is empty and a single native set is provided,
    // return it directly to preserve reference identity (matches immutable.js behavior in some cases).
    if (this._set.size === 0 && collections.length === 1) {
      const only = collections[0];
      if (isNativeSet(only)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return only as any;
      }
    }
    const newSet = new globalThis.Set(this._set);
    for (const collection of collections) {
      for (const value of collection) {
        newSet.add(value);
      }
    }
    return new NativeSetImpl<T>(newSet);
  }

  intersect(...collections: Array<Iterable<T>>): ISet<T> {
    const newSet = new globalThis.Set<T>();
    for (const value of this._set) {
      const inAll = collections.every((collection) => {
        for (const v of collection) {
          if (is(v, value)) {
            return true;
          }
        }
        return false;
      });
      if (inAll) {
        newSet.add(value);
      }
    }
    return new NativeSetImpl<T>(newSet);
  }

  subtract(...collections: Array<Iterable<T>>): ISet<T> {
    const newSet = new globalThis.Set(this._set);
    for (const collection of collections) {
      for (const value of collection) {
        for (const v of newSet) {
          if (is(v, value)) {
            newSet.delete(v);
            break;
          }
        }
      }
    }
    return new NativeSetImpl<T>(newSet);
  }

  merge<U>(...collections: Array<Iterable<U>>): ISet<T | U> {
    const newSet = new globalThis.Set<T | U>();
    for (const item of this._set) {
      newSet.add(item);
    }
    for (const collection of collections) {
      for (const item of collection) {
        newSet.add(item);
      }
    }
    return new NativeSetImpl<T | U>(newSet) as unknown as ISet<T | U>;
  }

  concat<U>(...collections: Array<Iterable<U>>): ISet<T | U> {
    const newSet = new globalThis.Set<T | U>();
    for (const item of this._set) {
      newSet.add(item);
    }
    for (const collection of collections) {
      for (const item of collection) {
        newSet.add(item);
      }
    }
    return new NativeSetImpl<T | U>(newSet) as unknown as ISet<T | U>;
  }

  isSuperset(other: Iterable<T>): boolean {
    for (const value of other) {
      if (!this.has(value)) {
        return false;
      }
    }
    return true;
  }

  isSubset(other: Iterable<T>): boolean {
    const otherSet = Set(other);
    for (const value of this._set) {
      if (!otherSet.has(value)) {
        return false;
      }
    }
    return true;
  }

  // Transformation
  map<M>(mapper: (value: T, key: T, iter: ISet<T>) => M, context?: unknown): ISet<M> {
    const newSet = new globalThis.Set<M>();
    for (const value of this._set) {
      newSet.add(mapper.call(context, value, value, this as unknown as ISet<T>));
    }
    return new NativeSetImpl<M>(newSet);
  }

  filter(predicate: (value: T, key: T, iter: ISet<T>) => boolean, context?: unknown): ISet<T> {
    const newSet = new globalThis.Set<T>();
    for (const value of this._set) {
      if (predicate.call(context, value, value, this as unknown as ISet<T>)) {
        newSet.add(value);
      }
    }
    return new NativeSetImpl<T>(newSet);
  }

  filterNot(predicate: (value: T, key: T, iter: ISet<T>) => boolean, context?: unknown): ISet<T> {
    return this.filter((v, k, i) => !predicate.call(context, v, k, i), context);
  }

  // Sorting - returns OrderedSet since sorting creates order
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sort(comparator?: (a: any, b: any) => number): IOrderedSet<T> {
    const sorted = Array.from(this._set).sort(comparator);
    return new NativeSetImpl(new globalThis.Set(sorted)) as unknown as IOrderedSet<T>;
  }

  // Reduce
  reduce<R>(reducer: (reduction: R, value: T, key: T, iter: ISet<T>) => R, initialReduction: R, context?: unknown): R;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reduce<R>(reducer: (reduction: T | R, value: T, key: T, iter: ISet<T>) => R): R;
  reduce<R>(
    reducer: (reduction: R | T, value: T, key: T, iter: ISet<T>) => R,
    initialReduction?: R,
    context?: unknown,
  ): R {
    if (initialReduction === undefined) {
      // No initial value - use first element
      const iterator = this._set.values();
      const first = iterator.next();
      if (first.done) {
        throw new TypeError('Reduce of empty Set with no initial value');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let reduction: any = first.value;
      for (const value of iterator) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reduction = reducer.call(context, reduction, value, value, this as unknown as ISet<T>);
      }
      return reduction;
    }
    let reduction: R = initialReduction;
    for (const value of this._set) {
      reduction = reducer.call(context, reduction, value, value, this as unknown as ISet<T>);
    }
    return reduction;
  }

  // Search
  find(predicate: (value: T, key: T, iter: ISet<T>) => boolean, context?: unknown, notSetValue?: T): T | undefined {
    for (const value of this._set) {
      if (predicate.call(context, value, value, this as unknown as ISet<T>)) {
        return value;
      }
    }
    return notSetValue;
  }

  some(predicate: (value: T, key: T, iter: ISet<T>) => boolean, context?: unknown): boolean {
    for (const value of this._set) {
      if (predicate.call(context, value, value, this as unknown as ISet<T>)) {
        return true;
      }
    }
    return false;
  }

  every(predicate: (value: T, key: T, iter: ISet<T>) => boolean, context?: unknown): boolean {
    for (const value of this._set) {
      if (!predicate.call(context, value, value, this as unknown as ISet<T>)) {
        return false;
      }
    }
    return true;
  }

  // Iteration
  forEach(fn: (value: T, key: T, iter: ISet<T>) => void, context?: unknown): number {
    let count = 0;
    for (const value of this._set) {
      fn.call(context, value, value, this);
      count++;
    }
    return count;
  }

  // Read helpers
  first(): T | undefined {
    return this._set.values().next().value;
  }

  values(): IterableIterator<T> {
    return this._set.values();
  }

  // Slicing - returns OrderedSet to maintain order
  take(amount: number): IOrderedSet<T> {
    const result: T[] = [];
    let count = 0;
    for (const value of this._set) {
      if (count >= amount) {
        break;
      }
      result.push(value);
      count++;
    }
    return new NativeSetImpl(new globalThis.Set(result)) as unknown as IOrderedSet<T>;
  }

  isEmpty(): boolean {
    return this._set.size === 0;
  }

  count(): number {
    return this._set.size;
  }

  // Conversion
  toArray(): T[] {
    return Array.from(this._set);
  }

  toJS(): T[] {
    return this.toArray();
  }

  toJSON(): T[] {
    return this.toArray();
  }

  toSet(): ISet<T> {
    return this;
  }

  valueSeq(): ISeq<T> {
    return List(Array.from(this._set)) as unknown as ISeq<T>;
  }

  toOrderedSet(): IOrderedSet<T> {
    return OrderedSet(this._set);
  }

  toList(): IList<T> {
    return List(this._set);
  }

  join(separator = ','): string {
    return Array.from(this._set).join(separator);
  }

  // Batch mutations
  withMutations(mutator: (mutable: ISet<T>) => unknown): ISet<T> {
    const mutableSet = new globalThis.Set(this._set);
    const mutable = new NativeSetImpl<T>(mutableSet);

    // Override methods to mutate in place
    mutable.add = function (value: T): ISet<T> {
      mutableSet.add(value);
      return this;
    };
    mutable.delete = function (value: T): ISet<T> {
      for (const v of mutableSet) {
        if (is(v, value)) {
          mutableSet.delete(v);
          break;
        }
      }
      return this;
    };

    mutator(mutable);

    return new NativeSetImpl<T>(mutableSet);
  }

  asMutable(): ISet<T> {
    return this;
  }

  asImmutable(): ISet<T> {
    return this;
  }

  // Comparison
  equals(other: unknown): boolean {
    if (!isNativeSet(other)) {
      return false;
    }
    if (this._set.size !== other._set.size) {
      return false;
    }
    for (const value of this._set) {
      if (!other.has(value)) {
        return false;
      }
    }
    return true;
  }

  hashCode(): number {
    let hash = 0;
    for (const value of this._set) {
      // eslint-disable-next-line no-bitwise
      hash = (hash + hashHelper(value)) | 0;
    }
    return hash;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function toJSHelper(value: unknown): unknown {
  if (isNativeMap(value) || isNativeList(value) || isNativeSet(value)) {
    return value.toJS();
  }
  if (Array.isArray(value)) {
    return value.map(toJSHelper);
  }
  if (value && typeof value === 'object' && 'toJS' in value && typeof value.toJS === 'function') {
    return value.toJS();
  }
  return value;
}

function hashHelper(value: unknown): number {
  if (value === null) {
    return 0;
  }
  if (value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    // eslint-disable-next-line no-bitwise
    return value | 0;
  }
  if (typeof value === 'string') {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      // eslint-disable-next-line no-bitwise
      hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return hash;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (isNativeCollection(value)) {
    // @ts-expect-error - Type narrowing limitation
    return value.hashCode();
  }
  if (typeof value === 'object' && value !== null) {
    if ('hashCode' in value && typeof value.hashCode === 'function') {
      return value.hashCode();
    }
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length;
  }
  return 0;
}

// ============================================================================
// Exported Factory Functions
// ============================================================================

// Function overloads to match immutable.js signature
// Multiple overloads needed for proper type inference
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function Map<K, V>(collection: Iterable<[K, V]>): IMap<K, V>;
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function Map<V>(obj: { [key: string]: V }): IMap<string, V>;
export function Map<K, V>(): IMap<K, V>;
export function Map(): IMap<any, any>;
export function Map<K = unknown, V = unknown>(
  entries?: Iterable<[K, V]> | { [key: string]: V },
): IMap<K, V> | IMap<string, V> | IMap<any, any> {
  if (!entries) {
    return new NativeMapImpl(new globalThis.Map());
  }
  // Check if it's a plain object (not iterable)
  if (typeof entries === 'object' && !(Symbol.iterator in entries)) {
    return new NativeMapImpl(new globalThis.Map(Object.entries(entries)));
  }
  return new NativeMapImpl(new globalThis.Map(entries));
}

// Function overloads to match immutable.js signature (EXACT order matters!)
// Multiple overloads needed for proper type inference
export function List(): IList<any>; // 1. No args, no type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function List<T>(): IList<T>; // 2. No args, explicit type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function List<T>(collection: Iterable<T>): IList<T>; // 3. With args
export function List<T = any>(values?: Iterable<T>): IList<T> {
  return new NativeListImpl(values ? Array.from(values) : []);
}

// Function overloads to match immutable.js signature (EXACT order matters!)
// Multiple overloads needed for proper type inference
export function Set(): ISet<any>; // 1. No args, no type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function Set<T>(): ISet<T>; // 2. No args, explicit type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function Set<T>(collection: Iterable<T>): ISet<T>; // 3. With args
export function Set<T = any>(values?: Iterable<T>): ISet<T> {
  return new NativeSetImpl(values ? new globalThis.Set(values) : new globalThis.Set());
}

// Multiple overloads needed for proper type inference
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function OrderedMap<K, V>(entries: Iterable<[K, V]>): IOrderedMap<K, V>;
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function OrderedMap<V>(obj: { [key: string]: V }): IOrderedMap<string, V>;
export function OrderedMap<K, V>(): IOrderedMap<K, V>;
export function OrderedMap(): IOrderedMap<any, any>; // No-type-args overload
export function OrderedMap<K = unknown, V = unknown>(
  entries?: Iterable<[K, V]> | { [key: string]: V },
): IOrderedMap<K, V> | IOrderedMap<string, V> | IOrderedMap<any, any> {
  if (!entries) {
    return new NativeMapImpl(new globalThis.Map()) as IOrderedMap<K, V>;
  }
  // Check if it's a plain object (not iterable)
  if (typeof entries === 'object' && !(Symbol.iterator in entries)) {
    return new NativeMapImpl(new globalThis.Map(Object.entries(entries) as Array<[K, V]>)) as IOrderedMap<
      string,
      V
    > as IOrderedMap<K, V>;
  }
  return new NativeMapImpl(new globalThis.Map(entries)) as IOrderedMap<K, V>;
}

// Function overloads to match immutable.js signature (EXACT order matters!)
// Multiple overloads needed for proper type inference
export function OrderedSet(): IOrderedSet<any>; // 1. No args, no type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function OrderedSet<T>(): IOrderedSet<T>; // 2. No args, explicit type param
// eslint-disable-next-line @typescript-eslint/unified-signatures
export function OrderedSet<T>(collection: Iterable<T>): IOrderedSet<T>; // 3. With args
export function OrderedSet<T = any>(values?: Iterable<T>): IOrderedSet<T> {
  return new NativeSetImpl(values ? new globalThis.Set(values) : new globalThis.Set()) as IOrderedSet<T>;
}

// Record will be implemented in the next stage

// ============================================================================
// Static Functions
// ============================================================================

export function fromJS<T>(value: T): FromJS<T> {
  if (value === null || value === undefined) {
    return value as FromJS<T>;
  }

  // If it's already one of our native immutable collections (Map/List/Set/Record), return as-is
  if (isNativeCollection(value)) {
    return value as unknown as FromJS<T>;
  }

  if (typeof value !== 'object') {
    return value as FromJS<T>;
  }

  if (Array.isArray(value)) {
    return List(value.map((v) => fromJS(v))) as FromJS<T>;
  }

  // Native Set -> OrderedSet
  if (value instanceof globalThis.Set) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items = Array.from(value as unknown as Set<any>).map((v) => fromJS(v));
    return OrderedSet(items) as unknown as FromJS<T>;
  }

  // Plain object -> Map
  // Match Immutable.js iteration order:
  // - For small maps (<=8 entries): preserve insertion order (ArrayMapNode behavior)
  // - For larger maps: use HAMT-based ordering (hash bits determine bucket positions)
  const entries = Object.entries(value).map(([k, v]) => [k, fromJS(v)] as [string, unknown]);
  if (entries.length > MAX_ARRAY_MAP_SIZE) {
    entries.sort((a, b) => compareByHamtOrder(a[0], b[0]));
  }
  return Map(entries) as FromJS<T>;
}

export function is(first: unknown, second: unknown): boolean {
  // Match immutable.js `is` semantics:
  // - SameValue equality (handles NaN/-0)
  // - Delegates to `.equals` when present (Immutable collections / Records)
  // - Falls back to comparing `valueOf()` when it produces a primitive/value (e.g. Date, boxed primitives)
  // - Does NOT deep-compare plain objects/arrays
  if (Object.is(first, second)) {
    return true;
  }

  // Jest/Vitest asymmetric matchers support
  // If either side is an asymmetric matcher (has asymmetricMatch function), delegate to it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeMatcher1 = first as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maybeMatcher2 = second as any;
  if (maybeMatcher1 && typeof maybeMatcher1 === 'object' && typeof maybeMatcher1.asymmetricMatch === 'function') {
    try {
      return !!maybeMatcher1.asymmetricMatch(second);
    } catch {
      return false;
    }
  }
  if (maybeMatcher2 && typeof maybeMatcher2 === 'object' && typeof maybeMatcher2.asymmetricMatch === 'function') {
    try {
      return !!maybeMatcher2.asymmetricMatch(first);
    } catch {
      return false;
    }
  }

  // Null/undefined
  if (first === null || first === undefined || second === null || second === undefined) {
    return false;
  }

  // Native collections (type guard ensures both have equals method)
  if (isNativeCollection(first) && isNativeCollection(second)) {
    // @ts-expect-error - Type narrowing limitation
    return first.equals(second);
  }

  // Other collections with equals method
  if (first && typeof first === 'object') {
    if ('equals' in first && typeof first.equals === 'function' && second && typeof second === 'object') {
      return first.equals(second);
    }
  }

  // valueOf() fallback (immutable.js uses valueOf() when it returns a non-identical value)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v1 = (first as any)?.valueOf?.();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v2 = (second as any)?.valueOf?.();
  if (v1 !== first || v2 !== second) {
    return Object.is(v1, v2);
  }

  return false;
}

export function isImmutable(value: unknown): value is ICollection<any, any> {
  return isNativeCollection(value);
}

// Type guard functions for native collections
export function isMap(value: unknown): value is IMap<unknown, unknown> {
  return isNativeMap(value) && !isNativeList(value) && !isNativeSet(value);
}

export function isList(value: unknown): value is IList<unknown> {
  return isNativeList(value);
}

export function isSet(value: unknown): value is ISet<unknown> {
  return isNativeSet(value);
}

export function isOrderedSet(value: unknown): value is IOrderedSet<unknown> {
  // In native impl, all Sets are ordered (they use native Set which maintains insertion order)
  return isNativeSet(value);
}

// ============================================================================
// Legacy Helpers
// ============================================================================

// Overloads matching immutable.js
export function get<K, V>(collection: ICollection<K, V>, key: K): V | undefined;
export function get<K, V, NSV>(collection: ICollection<K, V>, key: K, notSetValue: NSV): V | NSV;
export function get<V>(collection: V[], key: number): V | undefined;
export function get<V, NSV>(collection: V[], key: number, notSetValue: NSV): V | NSV;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function get<C extends object, K extends keyof C>(object: C, key: K, notSetValue: any): C[K];
export function get<V>(collection: { [key: string]: V }, key: string): V | undefined;
export function get<V, NSV>(collection: { [key: string]: V }, key: string, notSetValue: NSV): V | NSV;
export function get(collection: unknown, key: unknown, notSetValue?: unknown): unknown {
  if (isNativeMap(collection)) {
    return collection.get(key as never, notSetValue as never);
  }
  if (isNativeList(collection)) {
    return collection.get(key as number, notSetValue as never);
  }
  // Support plain arrays
  if (Array.isArray(collection)) {
    const idx = key as number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const array = collection;
    return idx in array ? array[idx] : notSetValue;
  }
  // Support plain objects
  if (collection !== null && typeof collection === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = collection as any;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (key as string) in obj ? obj[key as keyof typeof obj] : notSetValue;
  }
  return notSetValue;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getIn(collection: any, keyPath: KeyPath, notSetValue: any): any {
  return getInHelper(collection, keyPath, notSetValue);
}

// Multiple overloads matching immutable.js
export function set<K, V, C extends ICollection<K, V>>(collection: C, key: K, value: V): C;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function set<V, C extends V[]>(collection: C, key: number, value: V): C;
export function set<C, K extends keyof C>(object: C, key: K, value: C[K]): C;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function set<V, C extends { [key: string]: V }>(collection: C, key: string, value: V): C;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function set(collection: any, key: any, value: any): any {
  // Route through setInHelper for consistent semantics across Maps, Lists, Records, and plain objects.
  return setInHelper(collection, [key], value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setIn<C>(collection: C, keyPath: KeyPath, value: any): C {
  // Use the generic helper which delegates to collection.setIn when available
  // and supports NativeMap, NativeList, Records, and plain objects.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return setInHelper(collection as any, keyPath, value) as C;
}

export const Collection = {
  isIndexed: (value: unknown): boolean => isNativeList(value),
  isKeyed: (value: unknown): boolean => isNativeMap(value),
};

export const Seq = {
  of: <T>(...values: T[]): ISeq<T> => new NativeSeq(values),
};

export const Iterable = {
  isIndexed: (value: unknown): boolean => isNativeList(value),
  isKeyed: (value: unknown): boolean => isNativeMap(value),
};

// ============================================================================
// Native Record Implementation
// ============================================================================

export function Record<T extends object>(defaults: T, name?: string): RecordFactory<T> {
  // Create the Record class
  class RecordType {
    _data!: Map<keyof T, T[keyof T]>; // Initialized via defineProperty in constructor
    private static readonly _defaults: T = defaults;
    private static readonly _keys: Array<keyof T> = Object.keys(defaults) as Array<keyof T>;

    constructor(values?: Partial<T> | Iterable<[string, unknown]>) {
      // Allow calling without 'new' keyword (matches immutable.js behavior)
      if (!(this instanceof RecordType)) {
        return new RecordType(values);
      }

      // Use defineProperty to make _data configurable for withMutations
      Object.defineProperty(this, '_data', {
        value: new globalThis.Map<keyof T, T[keyof T]>(),
        configurable: true, // Allows redefinition in withMutations
        writable: false,
        enumerable: false,
      });

      // Initialize with defaults
      for (const key of RecordType._keys) {
        this._data.set(key, defaults[key]);
      }

      // Override with provided values
      if (values) {
        if (Symbol.iterator in values) {
          // Iterable of entries
          for (const [key, value] of values) {
            if (key in defaults) {
              if (value !== undefined) {
                this._data.set(key as keyof T, value as T[keyof T]);
              }
            }
          }
        } else {
          // Plain object
          for (const key of Object.keys(values) as Array<keyof T>) {
            if (key in defaults) {
              // Only override defaults when a defined value is provided
              if (values[key] !== undefined) {
                this._data.set(key, values[key] as T[keyof T]);
              }
            }
          }
        }
      }

      // Don't freeze - immutable.js doesn't freeze Records
      // Immutability enforced by: property setters (throw errors) + methods returning new instances
    }

    // Map-like methods
    get<K extends keyof T>(key: K, notSetValue?: T[K]): T[K] {
      const value = this._data.get(key);
      // @ts-expect-error - Type narrowing issue with notSetValue
      return value !== undefined ? value : notSetValue !== undefined ? notSetValue : defaults[key];
    }

    getIn(keyPath: KeyPath, notSetValue?: unknown): unknown {
      return getInHelper(this, keyPath, notSetValue);
    }

    has(key: keyof T): boolean {
      return this._data.has(key);
    }

    hasIn(keyPath: KeyPath): boolean {
      return hasInHelper(this, keyPath);
    }

    includes(value: T[keyof T]): boolean {
      for (const v of this._data.values()) {
        if (is(v, value)) {
          return true;
        }
      }
      return false;
    }

    contains(value: T[keyof T]): boolean {
      return this.includes(value);
    }

    set<K extends keyof T>(key: K, value: T[K]): this {
      const newValues: Partial<T> = {};
      for (const k of RecordType._keys) {
        newValues[k] = this._data.get(k);
      }
      newValues[key] = value;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)(newValues) as this;
    }

    setIn(keyPath: KeyPath, value: unknown): this {
      const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
      if (keys.length === 0) {
        return this;
      }
      const [firstKey, ...restKeys] = keys as [keyof T, ...unknown[]];
      if (restKeys.length === 0) {
        return this.set(firstKey, value as T[keyof T]);
      }
      const current = this.get(firstKey);
      if (isNativeMap(current) || isNativeList(current)) {
        const updated = setInHelper(current, restKeys, value);
        return this.set(firstKey, updated as T[keyof T]);
      }
      if (current && typeof current === 'object' && 'setIn' in (current as object)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updated = (current as any).setIn(restKeys, value);
        return this.set(firstKey, updated as T[keyof T]);
      }
      return this;
    }

    delete(key: keyof T): this {
      const newValues: Partial<T> = {};
      for (const k of RecordType._keys) {
        if (k !== key) {
          newValues[k] = this._data.get(k);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)(newValues) as this;
    }

    remove(key: keyof T): this {
      return this.delete(key);
    }

    deleteIn(keyPath: KeyPath): this {
      const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
      if (keys.length === 0) {
        return this;
      }
      const [firstKey, ...restKeys] = keys as [keyof T, ...unknown[]];
      if (restKeys.length === 0) {
        return this.delete(firstKey);
      }
      const current = this.get(firstKey);
      if (isNativeMap(current) || isNativeList(current)) {
        const updated = deleteInHelper(current, restKeys);
        return this.set(firstKey, updated as T[keyof T]);
      }
      if (current && typeof current === 'object' && 'deleteIn' in (current as object)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updated = (current as any).deleteIn(restKeys);
        return this.set(firstKey, updated as T[keyof T]);
      }
      return this;
    }

    clear(): this {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)() as this;
    }

    update(updater: (value: this) => this): this;
    update<K extends keyof T>(key: K, updater: (value: T[K]) => T[K]): this;
    update<K extends keyof T>(key: K, notSetValue: T[K], updater: (value: T[K]) => T[K]): this;
    update(...args: unknown[]): this {
      if (args.length === 1) {
        const updater = args[0] as (value: this) => this;
        return updater(this);
      }
      if (args.length === 2) {
        const [key, updater] = args as [keyof T, (value: T[keyof T]) => T[keyof T]];
        const value = this.get(key);
        return this.set(key, updater(value));
      }
      const [key, notSetValue, updater] = args as [keyof T, T[keyof T], (value: T[keyof T]) => T[keyof T]];
      const value = this.get(key, notSetValue);
      return this.set(key, updater(value));
    }

    updateIn(keyPath: KeyPath, updater: (value: unknown) => unknown): this;
    updateIn(keyPath: KeyPath, notSetValue: unknown, updater: (value: unknown) => unknown): this;
    updateIn(...args: unknown[]): this {
      const keyPath = args[0] as KeyPath;
      const keys = Array.isArray(keyPath) ? keyPath : Array.from(keyPath);
      const hasNotSet = args.length === 3;
      const notSetValue = hasNotSet ? args[1] : undefined;
      const updater = (hasNotSet ? args[2] : args[1]) as (value: unknown) => unknown;
      if (keys.length === 0) {
        return this;
      }
      const [firstKey, ...restKeys] = keys as [keyof T, ...unknown[]];
      if (restKeys.length === 0) {
        const current = this.get(firstKey, notSetValue as T[keyof T]);
        return this.set(firstKey, updater(current) as T[keyof T]);
      }
      // For intermediate keys, do NOT apply notSetValue; it only applies to the final key
      const current = this.get(firstKey);
      if (isNativeMap(current) || isNativeList(current)) {
        const updated = updateInHelper(current, restKeys, notSetValue, updater);
        return this.set(firstKey, updated as T[keyof T]);
      }
      if (current && typeof current === 'object' && 'updateIn' in (current as object)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updated = (current as any).updateIn(restKeys, notSetValue, updater);
        return this.set(firstKey, updated as T[keyof T]);
      }
      return this;
    }

    merge(...collections: Array<Partial<T> | Iterable<[keyof T, T[keyof T]]>>): this {
      const newValues: Partial<T> = {};
      for (const key of RecordType._keys) {
        newValues[key] = this._data.get(key);
      }

      for (const collection of collections) {
        if (Symbol.iterator in collection) {
          for (const [key, value] of collection) {
            if (key in defaults) {
              newValues[key] = value;
            }
          }
        } else {
          for (const key of Object.keys(collection) as Array<keyof T>) {
            if (key in defaults) {
              newValues[key] = collection[key] as T[keyof T];
            }
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)(newValues) as this;
    }

    mergeDeep(...collections: Array<Partial<T> | Iterable<[keyof T, T[keyof T]]>>): this {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      let result: this = this;
      // Helpers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isPlainObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v) && !isNativeCollection(v);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalize = (v: any) => {
        if (isNativeCollection(v)) {
          return v;
        }
        if (Array.isArray(v)) {
          return List(v);
        }
        if (isPlainObject(v)) {
          return Map(v);
        }
        return v;
      };
      for (const collection of collections) {
        const entries = Symbol.iterator in collection ? collection : Object.entries(collection);
        for (const [key, rawValue] of entries as Iterable<[keyof T, T[keyof T]]>) {
          if (!(key in defaults)) {
            continue;
          }
          if (rawValue === undefined) {
            // Skip undefined to avoid overwriting defaults (align with expected reducer behavior)
            continue;
          }
          const value = normalize(rawValue);
          const existing = result.get(key);
          // 1) Map-like field: recurse
          if (isNativeMap(existing) && isNativeMap(value)) {
            result = result.set(
              key,
              (existing as NativeMapImpl<unknown, unknown>).mergeDeep(
                value.toObject() as { [key: string]: unknown },
              ) as unknown as T[keyof T],
            );
            continue;
          }
          // 2) List-like field: append union (preserve existing order, no dups)
          if (isNativeList(existing) && (isNativeList(value) || Array.isArray(value))) {
            const existingList = existing as unknown as NativeListImpl<unknown>;
            const incomingList = isNativeList(value)
              ? (value as unknown as NativeListImpl<unknown>).toArray()
              : (value as unknown as unknown[]);
            if (incomingList.length === 0) {
              // no-op for empty incoming lists (do not remove existing entries)
              continue;
            }
            const existingItems = existingList.toArray();
            const existingSet = new globalThis.Set(existingItems.map((x) => JSON.stringify(x)));
            const toAppend = incomingList.filter((x) => !existingSet.has(JSON.stringify(x)));
            const merged = List([...existingItems, ...toAppend]);
            result = result.set(key, merged as unknown as T[keyof T]);
            continue;
          }
          // 3) Existing is a collection that supports mergeDeep (e.g., nested Record): delegate
          // Only delegate if value is also an object - primitives should fall through to replacement
          // This matches Immutable.js behavior where merging a collection with a primitive replaces the collection
          if (existing && typeof existing === 'object' && 'mergeDeep' in (existing as object)) {
            if (value && typeof value === 'object') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const merged = (existing as any).mergeDeep(value);
              result = result.set(key, merged as T[keyof T]);
              continue;
            }
            // If value is a primitive, fall through to replacement (case 4)
          }
          // 4) Fallback: replace field
          result = result.set(key, value);
        }
      }
      return result;
    }

    mergeWith(
      merger: (oldVal: T[keyof T], newVal: T[keyof T], key: keyof T) => T[keyof T],
      ...collections: Array<Partial<T> | Iterable<[keyof T, T[keyof T]]>>
    ): this {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      let result: this = this;
      for (const collection of collections) {
        const entries = Symbol.iterator in collection ? collection : Object.entries(collection);
        for (const [key, newVal] of entries as Iterable<[keyof T, T[keyof T]]>) {
          if (key in defaults) {
            const oldVal = result.get(key);
            const mergedVal = merger(oldVal, newVal, key);
            result = result.set(key, mergedVal);
          }
        }
      }
      return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mergeIn(keyPath: Iterable<any>, ...collections: any[]): this {
      const existing = this.getIn(keyPath);
      if (existing && typeof existing === 'object' && 'merge' in existing && typeof existing.merge === 'function') {
        return this.setIn(keyPath, existing.merge(...collections));
      }
      return this;
    }

    map<M>(mapper: (value: T[keyof T], key: keyof T, iter: RecordType) => M): IMap<keyof T, M> {
      const entries: Array<[keyof T, M]> = [];
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        entries.push([key, mapper(value, key, this)]);
      }
      return Map(entries);
    }

    filter(predicate: (value: T[keyof T], key: keyof T, iter: this) => boolean): this {
      const newValues: Partial<T> = {};
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        if (predicate(value, key, this)) {
          newValues[key] = value;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)(newValues) as this;
    }

    filterNot(predicate: (value: T[keyof T], key: keyof T, iter: this) => boolean): this {
      return this.filter((v, k, i) => !predicate(v, k, i));
    }

    reduce<R>(reducer: (reduction: R, value: T[keyof T], key: keyof T, iter: RecordType) => R, initialReduction: R): R {
      let reduction = initialReduction;
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        reduction = reducer(reduction, value, key, this);
      }
      return reduction;
    }

    find(
      predicate: (value: T[keyof T], key: keyof T, iter: RecordType) => boolean,
      notSetValue?: T[keyof T],
    ): T[keyof T] | undefined {
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        if (predicate(value, key, this)) {
          return value;
        }
      }
      return notSetValue;
    }

    findIndex(predicate: (value: T[keyof T], key: keyof T, iter: RecordType) => boolean): keyof T | undefined {
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        if (predicate(value, key, this)) {
          return key;
        }
      }
      return undefined;
    }

    findKey(predicate: (value: T[keyof T], key: keyof T, iter: RecordType) => boolean): keyof T | undefined {
      return this.findIndex(predicate);
    }

    some(predicate: (value: T[keyof T], key: keyof T, iter: RecordType) => boolean): boolean {
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        if (predicate(value, key, this)) {
          return true;
        }
      }
      return false;
    }

    every(predicate: (value: T[keyof T], key: keyof T, iter: RecordType) => boolean): boolean {
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        if (!predicate(value, key, this)) {
          return false;
        }
      }
      return true;
    }

    forEach(fn: (value: T[keyof T], key: keyof T, iter: RecordType) => void): number {
      let count = 0;
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = this._data.get(key)!;
        fn(value, key, this);
        count++;
      }
      return count;
    }

    keySeq(): ISeq<keyof T> {
      return new NativeSeq(Array.from(RecordType._keys));
    }

    valueSeq(): ISeq<T[keyof T]> {
      return new NativeSeq(Array.from(this._data.values()));
    }

    entrySeq(): ISeq<[keyof T, T[keyof T]]> {
      return new NativeSeq(Array.from(this._data.entries()));
    }

    keys(): IterableIterator<keyof T> {
      return this._data.keys();
    }

    values(): IterableIterator<T[keyof T]> {
      return this._data.values();
    }

    entries(): IterableIterator<[keyof T, T[keyof T]]> {
      return this._data.entries();
    }

    get size(): number {
      return this._data.size;
    }

    isEmpty(): boolean {
      return this._data.size === 0;
    }

    count(): number {
      return this._data.size;
    }

    toObject(): T {
      const obj = {} as T;
      for (const key of RecordType._keys) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        obj[key] = this._data.get(key)!;
      }
      return obj;
    }

    toArray(): Array<[keyof T, T[keyof T]]> {
      return Array.from(this._data.entries());
    }

    toJS(): { [K in keyof T]: unknown } {
      const obj = {} as { [K in keyof T]: unknown };
      for (const key of RecordType._keys) {
        obj[key] = toJSHelper(this._data.get(key));
      }
      return obj;
    }

    toJSON(): T {
      return this.toObject();
    }

    toSeq(): IMap<keyof T, T[keyof T]> {
      const entries: Array<[keyof T, T[keyof T]]> = [];
      for (const [key, value] of this._data) {
        entries.push([key, value]);
      }
      return Map(entries);
    }

    *[Symbol.iterator](): IterableIterator<[keyof T, T[keyof T]]> {
      yield* this._data.entries();
    }

    withMutations(mutator: (mutable: this) => unknown): this {
      const mutableData = new globalThis.Map(this._data);
      // Create a mutable instance that preserves the subclass prototype (e.g., ApprovalSettings methods)
      const mutable = Object.create(Object.getPrototypeOf(this)) as this;

      // Replace _data with our mutable copy (now possible since _data is configurable)
      Object.defineProperty(mutable, '_data', {
        value: mutableData,
        writable: true,
        configurable: true,
        enumerable: false,
      });

      // Override set to mutate in place and return the same instance for chaining
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mutable as any).set = function <K extends keyof T>(key: K, value: T[K]) {
        mutableData.set(key, value);
        return this;
      };

      // Override delete/remove to mutate in place (reset to default value)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mutable as any).delete = function <K extends keyof T>(key: K) {
        // Reset to default value instead of removing (Records always have all keys)
        mutableData.set(key, defaults[key]);
        return this;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mutable as any).remove = (mutable as any).delete;

      // Override merge to mutate in place
      // Without this, merge() returns a new Record and the result is discarded,
      // causing bugs like REL-12117 where webhook.mergeForm() returns unchanged data.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mutable as any).merge = function (...collections: Array<Partial<T> | Iterable<[keyof T, T[keyof T]]>>) {
        for (const collection of collections) {
          if (Symbol.iterator in collection) {
            for (const [key, value] of collection) {
              if (key in defaults) {
                mutableData.set(key, value);
              }
            }
          } else {
            for (const key of Object.keys(collection) as Array<keyof T>) {
              if (key in defaults) {
                mutableData.set(key, collection[key] as T[keyof T]);
              }
            }
          }
        }
        return this;
      };

      mutator(mutable);

      // Create new immutable instance from the mutated data using the same constructor
      const newValues: Partial<T> = {};
      for (const [key, value] of mutableData) {
        newValues[key] = value;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      return new (this.constructor as any)(newValues) as this;
    }

    asMutable(): RecordType {
      return this;
    }

    asImmutable(): RecordType {
      return this;
    }

    equals(other: unknown): boolean {
      // Record vs Record of same factory
      if (other instanceof RecordType) {
        if (this._data.size !== other._data.size) {
          return false;
        }
        for (const key of RecordType._keys) {
          if (!is(this._data.get(key), other._data.get(key))) {
            return false;
          }
        }
        return true;
      }
      // Record vs plain object (support deep-equality assertions against POJOs)
      if (other && typeof other === 'object' && Object.getPrototypeOf(other) === Object.prototype) {
        const otherKeys = Object.keys(other);
        if (otherKeys.length !== RecordType._keys.length) {
          return false;
        }
        for (const key of RecordType._keys) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const otherValue = (other as any)[String(key)];
          if (!is(this._data.get(key), otherValue)) {
            return false;
          }
        }
        return true;
      }
      return false;
    }

    hashCode(): number {
      let hash = 0;
      for (const [key, value] of this._data) {
        // eslint-disable-next-line no-bitwise
        hash = (hash + hashHelper(key) + hashHelper(value)) | 0;
      }
      return hash;
    }
  }

  // Define property getters and setters using Object.defineProperty
  for (const key of Object.keys(defaults) as Array<keyof T>) {
    Object.defineProperty(RecordType.prototype, key, {
      get(this: InstanceType<typeof RecordType>): T[keyof T] {
        return this.get(key);
      },
      set() {
        throw new Error(
          `Cannot set property '${String(key)}' directly on Record${name ? ` ${name}` : ''}. Use .set('${String(key)}', value) instead.`,
        );
      },
      enumerable: true,
      configurable: false,
    });
  }

  // Return a constructable AND callable wrapper that preserves subclassing semantics.
  // Using Reflect.construct ensures `new.target` is respected so extending classes
  // (e.g., `class Foo extends Record({...}) { ... }`) keep their prototype methods.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Factory: any = function (this: unknown, values?: Partial<T> | Iterable<[string, unknown]>) {
    // If called without `new`, behave like a constructor. Prefer Reflect.construct
    // when available to preserve `new.target` semantics for subclassing.
    const hasReflectConstruct =
      typeof Reflect !== 'undefined' &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !!(Reflect as unknown as { construct?: any }).construct;

    if (hasReflectConstruct) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newTarget: any = new.target || Factory;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-function-type
      return (Reflect as unknown as { construct: Function }).construct(RecordType, [values], newTarget);
    }

    // Fallback for environments without Reflect: simple construction
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return new RecordType(values);
  };
  Factory.prototype = RecordType.prototype;
  // Ensure static inheritance mirrors the inner class where relevant
  // (important for `instanceof` checks and subclassing behavior in some engines)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Object.setPrototypeOf(Factory, RecordType as unknown as any);
  return Factory as RecordFactory<T>;
}
